'use client';
import { useResource } from '@/lib/use-resource';

export interface InsiderData {
  buyCount: number; sellCount: number;
  buyShares: number; sellShares: number;
  buyValue: number; sellValue: number;
  netBuyRatio: number;
  lastBuyDate: string | null; lastSellDate: string | null;
  form4Count: number;
  dateRange?: { start: string; end: string };
}

export function InsiderDetail({ symbol }: { symbol: string }) {
  const { data, loading } = useResource<InsiderData>(`/api/insider-trading?symbol=${symbol}`);

  if (loading) return <div className="text-[8px] text-[#64748b] py-1 pl-2">Loading...</div>;
  if (!data) return <div className="text-[8px] text-[#64748b] py-1 pl-2">No SEC data for {symbol}</div>;

  const fmtMoney = (v: number) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`;
  const fmtShares = (v: number) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${v}`;

  return (
    <div className="ml-2 mb-1 p-2 rounded text-[9px] leading-relaxed" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
      <div className="text-[8px] text-[#64748b] mb-1.5">
        SEC Form 4 &middot; {data.dateRange?.start} to {data.dateRange?.end} &middot; {data.form4Count} filings
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {data.buyCount > 0 && (
          <>
            <div className="text-[#22c55e]">Insider Buys</div>
            <div className="text-[#22c55e] text-right font-semibold">{fmtMoney(data.buyValue)}</div>
            <div className="text-[#64748b] pl-2">Transactions</div>
            <div className="text-[#e2e8f0] text-right">{data.buyCount}</div>
            <div className="text-[#64748b] pl-2">Shares</div>
            <div className="text-[#e2e8f0] text-right">{fmtShares(data.buyShares)}</div>
            <div className="text-[#64748b] pl-2">Last Buy</div>
            <div className="text-[#e2e8f0] text-right">{data.lastBuyDate || '--'}</div>
          </>
        )}
        {data.sellCount > 0 && (
          <>
            <div className={`text-[#ef4444] ${data.buyCount > 0 ? 'mt-1' : ''}`}>Insider Sells</div>
            <div className={`text-[#ef4444] text-right font-semibold ${data.buyCount > 0 ? 'mt-1' : ''}`}>{fmtMoney(data.sellValue)}</div>
            <div className="text-[#64748b] pl-2">Transactions</div>
            <div className="text-[#e2e8f0] text-right">{data.sellCount}</div>
            <div className="text-[#64748b] pl-2">Shares</div>
            <div className="text-[#e2e8f0] text-right">{fmtShares(data.sellShares)}</div>
            <div className="text-[#64748b] pl-2">Last Sell</div>
            <div className="text-[#e2e8f0] text-right">{data.lastSellDate || '--'}</div>
          </>
        )}
        {data.buyCount === 0 && data.sellCount === 0 && (
          <>
            <div className="text-[#64748b]">No insider buys or sells</div>
            <div className="text-[#64748b] text-right">{data.form4Count} option/gift filings only</div>
          </>
        )}
      </div>
      <div className="mt-1.5 pt-1 border-t border-[#1e293b] flex justify-between">
        <span className="text-[#64748b]">Net Buy Ratio</span>
        <span className={`font-semibold ${data.netBuyRatio > 0 ? 'text-[#22c55e]' : data.netBuyRatio < 0 ? 'text-[#ef4444]' : 'text-[#64748b]'}`}>
          {data.netBuyRatio > 0 ? '+' : ''}{(data.netBuyRatio * 100).toFixed(0)}%
        </span>
      </div>
    </div>
  );
}

